import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { Car, ArrowRight, Phone, Mail, Lock, KeyRound } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("identifier"); // identifier, otp
  const [authMethod, setAuthMethod] = useState("pin"); // otp, pin
  const [identifier, setIdentifier] = useState("");
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState("");
  const [standardPin, setStandardPin] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return toast.error("Veuillez entrer votre email ou telephone");
    setLoading(true);
    try {
      const isEmail = identifier.includes("@");
      const payload = isEmail ? { email: identifier } : { telephone: identifier };
      const res = await api.post("/auth/request-otp", payload);
      setUserId(res.data.user_id);
      setStep("otp");
      toast.success(res.data.email_sent ? "Code OTP envoye par email. Verifiez votre boite de reception." : "Code OTP envoye");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Veuillez entrer le code a 6 chiffres");
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-otp", { user_id: userId, otp });
      login(res.data.token, res.data.user);
      toast.success("Connexion reussie");
      const role = res.data.user.role;
      navigate(role === "investisseur" ? "/investisseur" : role === "chauffeur" ? "/chauffeur-portal" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return toast.error("Veuillez entrer votre email ou telephone");
    if (!standardPin.trim()) return toast.error("Veuillez entrer votre code PIN");
    setLoading(true);
    try {
      const isEmail = identifier.includes("@");
      const payload = isEmail
        ? { email: identifier, pin: standardPin }
        : { telephone: identifier, pin: standardPin };
      const res = await api.post("/auth/pin-login", payload);
      login(res.data.token, res.data.user);
      toast.success("Connexion reussie");
      const role = res.data.user.role;
      navigate(role === "investisseur" ? "/investisseur" : role === "chauffeur" ? "/chauffeur-portal" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  const handleDriverLogin = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !pin.trim()) return toast.error("Telephone et PIN requis");
    setLoading(true);
    try {
      const res = await api.post("/auth/driver-login", { telephone: phone, pin });
      login(res.data.token, res.data.user);
      toast.success("Connexion reussie");
      navigate("/chauffeur-portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  const resetStandard = () => {
    setStep("identifier");
    setOtp("");
    setStandardPin("");
    setAuthMethod("otp");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 login-bg relative items-center justify-center p-12">
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Car className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold font-heading tracking-tight">VTC Fleet</h1>
          </div>
          <h2 className="text-4xl font-bold font-heading leading-tight mb-4">
            Gerez votre flotte VTC en toute simplicite
          </h2>
          <p className="text-lg text-blue-100 leading-relaxed">
            Suivez vos vehicules, chauffeurs et recettes depuis une seule plateforme intuitive.
          </p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/30 to-transparent" />
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-lg" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-2 lg:hidden">
              <div className="w-9 h-9 rounded-lg bg-[#0F52BA] flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold font-heading text-slate-900">VTC Fleet</span>
            </div>
            <CardTitle className="text-xl font-heading text-slate-900">Connexion</CardTitle>
            <CardDescription className="text-slate-500">Accedez a votre espace de gestion</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="standard" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="login-tabs">
                <TabsTrigger value="standard" onClick={resetStandard} data-testid="tab-standard">
                  <Mail className="w-4 h-4 mr-2" />Standard
                </TabsTrigger>
                <TabsTrigger value="chauffeur" data-testid="tab-chauffeur">
                  <Phone className="w-4 h-4 mr-2" />Chauffeur
                </TabsTrigger>
              </TabsList>

              <TabsContent value="standard">
                {step === "identifier" ? (
                  <div className="space-y-4">
                    {/* Auth method toggle */}
                    <div className="flex rounded-lg border border-slate-200 p-1 bg-slate-50" data-testid="auth-method-toggle">
                      <button
                        type="button"
                        onClick={() => setAuthMethod("otp")}
                        data-testid="method-otp"
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                          authMethod === "otp"
                            ? "bg-white shadow-sm text-[#0F52BA] border border-slate-200"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        <KeyRound className="w-4 h-4" />
                        Code OTP
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuthMethod("pin")}
                        data-testid="method-pin"
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                          authMethod === "pin"
                            ? "bg-white shadow-sm text-[#0F52BA] border border-slate-200"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        <Lock className="w-4 h-4" />
                        Code PIN
                      </button>
                    </div>

                    {authMethod === "otp" ? (
                      <form onSubmit={handleRequestOTP} className="space-y-4">
                        <div>
                          <Label htmlFor="identifier" className="text-slate-700">Email ou Telephone</Label>
                          <Input
                            id="identifier"
                            data-testid="input-identifier"
                            placeholder="admin@vtcfleet.com ou +33..."
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            className="mt-1.5"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#0F52BA] hover:bg-[#0A3D8F]" disabled={loading} data-testid="btn-request-otp">
                          {loading ? "Envoi..." : "Recevoir le code"}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </form>
                    ) : (
                      <form onSubmit={handlePinLogin} className="space-y-4">
                        <div>
                          <Label htmlFor="pin-identifier" className="text-slate-700">Email ou Telephone</Label>
                          <Input
                            id="pin-identifier"
                            data-testid="input-pin-identifier"
                            placeholder="admin@vtcfleet.com ou +33..."
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="standard-pin" className="text-slate-700">Code PIN</Label>
                          <Input
                            id="standard-pin"
                            data-testid="input-standard-pin"
                            type="password"
                            placeholder="4 ou 6 chiffres"
                            maxLength={6}
                            value={standardPin}
                            onChange={(e) => setStandardPin(e.target.value.replace(/\D/g, ""))}
                            className="mt-1.5"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#0F52BA] hover:bg-[#0A3D8F]" disabled={loading} data-testid="btn-pin-login">
                          {loading ? "Connexion..." : "Se connecter"}
                          <Lock className="w-4 h-4 ml-2" />
                        </Button>
                      </form>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <div>
                      <Label className="text-slate-700">Code de verification</Label>
                      <p className="text-xs text-slate-500 mb-3">Entrez le code a 6 chiffres</p>
                      <div className="flex justify-center">
                        <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="input-otp">
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#0F52BA] hover:bg-[#0A3D8F]" disabled={loading} data-testid="btn-verify-otp">
                      {loading ? "Verification..." : "Se connecter"}
                    </Button>
                    <Button type="button" variant="ghost" className="w-full text-slate-500" onClick={resetStandard} data-testid="btn-back">
                      Retour
                    </Button>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="chauffeur">
                <form onSubmit={handleDriverLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="phone" className="text-slate-700">Numero de telephone</Label>
                    <Input
                      id="phone"
                      data-testid="input-phone"
                      placeholder="+33 6 12 34 56 78"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pin" className="text-slate-700">Code PIN</Label>
                    <Input
                      id="pin"
                      data-testid="input-pin"
                      type="password"
                      placeholder="****"
                      maxLength={6}
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#0F52BA] hover:bg-[#0A3D8F]" disabled={loading} data-testid="btn-driver-login">
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
